import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getAvailableModels } from '../services/managerService';
import { useAuth } from './AuthContext';

const ModelContext = createContext();

export function ModelProvider({ children }) {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [defaultModel, setDefaultModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  // ModelProvider lives inside AuthProvider so it can react to login /
  // logout events. Without this, the first `/models` request can fire
  // before a token is in localStorage, fail with 401, and the selector
  // would stay hidden until the user manually reloads the page.
  const { isAuthenticated } = useAuth();

  const fetchModelsImpl = useCallback(async ({ silent = false } = {}) => {
    // On the initial load we surface a loading state so consumers can
    // hide the selector. Subsequent refreshes (after a config save, etc.)
    // run silently — we keep the existing models on screen so a transient
    // backend hiccup doesn't make the selector vanish.
    if (!silent) setLoading(true);
    try {
      const response = await getAvailableModels();
      if (response && response.status === 'success' && Array.isArray(response.models) && response.models.length > 0) {
        setModels(response.models);
        setDefaultModel(response.default_model);
        const validKeys = response.models.map(m => m.key);
        // Only re-pick selectedModel on the first successful load, or when
        // the current selection has been removed from the list. Otherwise
        // leave the user's selection alone.
        setSelectedModel((prev) => {
          if (prev && validKeys.includes(prev)) return prev;
          const stored = localStorage.getItem('selectedModel');
          if (stored && validKeys.includes(stored)) return stored;
          return response.default_model;
        });
        hasLoadedOnceRef.current = true;
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
      // Don't clear the existing list on a transient error — keep the
      // selector visible with whatever we last had. Only seed a fallback
      // selectedModel if we've never had a successful load.
      if (!hasLoadedOnceRef.current) {
        setSelectedModel((prev) => prev || 'claude-haiku-4.5');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Re-fetch whenever auth state flips: on login (so the selector
  // populates immediately) and on logout (so the next user starts fresh).
  useEffect(() => {
    if (isAuthenticated) {
      fetchModelsImpl();
    } else {
      // Reset so the next login triggers a non-silent (visible loading)
      // fetch instead of using stale data from the previous session.
      hasLoadedOnceRef.current = false;
      setModels([]);
      setSelectedModel(null);
      setDefaultModel(null);
    }
  }, [isAuthenticated, fetchModelsImpl]);

  const refreshModels = useCallback(() => fetchModelsImpl({ silent: true }), [fetchModelsImpl]);

  const changeModel = (modelKey) => {
    setSelectedModel(modelKey);
    localStorage.setItem('selectedModel', modelKey);
  };

  return (
    <ModelContext.Provider value={{ models, selectedModel, defaultModel, loading, changeModel, refreshModels }}>
      {children}
    </ModelContext.Provider>
  );
}

export function useModel() {
  const context = useContext(ModelContext);
  if (!context) {
    throw new Error('useModel must be used within a ModelProvider');
  }
  return context;
}

export default ModelContext;
